/**
 * Which aircraft hold the zones as their own fence, and the push that puts
 * them there.
 *
 * Sits under the zone list because a drawn zone and an enforced one look
 * identical on the map. The predecessor's operators could not tell them apart,
 * and the status here is the difference: "enforcing" only once the aircraft
 * itself reports its fence on and the zones it holds are the ones drawn now.
 */

import { useEffect, useState } from 'react';

import { getFenceStatus, pushFence, type FenceDrone, type FenceState, type FenceStatus } from '@/api/fence';
import { useZones } from '@/stores/missionStore';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';
const MUTED = 'rgba(244,244,245,0.45)';

const REFRESH_MS = 3000;

export const FENCE_LABELS: Record<FenceState, { label: string; color: string }> = {
  enforcing: { label: 'ENFORCING', color: OK },
  'report-only': { label: 'REPORT ONLY', color: WARN },
  breached: { label: 'BREACHED', color: DANGER },
  stale: { label: 'OLD ZONES', color: WARN },
  off: { label: 'FENCE OFF', color: WARN },
  unverified: { label: 'UNVERIFIED', color: WARN },
  'not-pushed': { label: 'NOT PUSHED', color: MUTED },
  failed: { label: 'PUSH FAILED', color: DANGER },
  'no-zones': { label: 'NO ZONES', color: MUTED },
};

function pushedAt(drone: FenceDrone): string | null {
  const push = drone.lastPush;
  if (!push) return null;
  const time = new Date(push.at * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `Pushed by ${push.by} at ${time}`;
}

export function FencePanel({ onMessage }: { onMessage: (text: string, warn?: boolean) => void }) {
  const zones = useZones();
  const [status, setStatus] = useState<FenceStatus | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  // Re-read when the zones change as well as on the timer, so an aircraft
  // holding the old set reads "old zones" as soon as somebody moves a corner.
  useEffect(() => {
    let cancelled = false;
    const load = () =>
      void getFenceStatus().then((result) => {
        if (!cancelled && result.ok) setStatus(result.data);
      });
    load();
    const id = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [zones, tick]);

  const push = (drone: FenceDrone, enable: boolean) => {
    setBusy(drone.droneId);
    void pushFence(drone.droneId, enable)
      .then((result) => {
        if (result.ok) {
          onMessage(`${drone.callsign}: ${result.data.message}`, !result.data.ok);
        } else {
          onMessage(`${drone.callsign}: ${result.error.message}`, true);
        }
      })
      .finally(() => {
        setBusy(null);
        setTick((n) => n + 1);
      });
  };

  const drones = status?.drones ?? [];

  return (
    <div>
      <div className="label-mono mb-2 text-[9px]">GEOFENCE ON BOARD</div>
      <p className="mb-2 text-[10px] leading-relaxed text-white/40">
        Zones are enforced here, before an order is sent. Push them to an aircraft you command and
        its autopilot enforces them too, with the link down. Nothing is pushed automatically.
      </p>
      {status?.zoneError ? (
        <p className="mb-2 text-[10px] leading-relaxed" style={{ color: DANGER }}>
          {status.zoneError}
        </p>
      ) : null}
      {drones.length === 0 ? (
        <p className="text-[10.5px] text-white/40">No aircraft connected.</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {drones.map((drone) => {
            const { label, color } = FENCE_LABELS[drone.state];
            const when = pushedAt(drone);
            const working = busy === drone.droneId;
            return (
              <div
                key={drone.droneId}
                data-testid={`fence-row-${drone.droneId}`}
                className="rounded-[12px] border border-white/8 bg-white/5 px-3 py-2"
              >
                <div className="flex items-center gap-2 text-[11.5px]">
                  <span className="min-w-0 truncate">{drone.callsign}</span>
                  {drone.simulated ? (
                    <span className="font-mono text-[8.5px] text-white/35">SIM</span>
                  ) : null}
                  <span
                    className="ml-auto rounded-full border px-2 py-[1px] font-mono text-[8.5px] font-bold"
                    style={{ color, borderColor: `color-mix(in oklch, ${color} 45%, transparent)` }}
                  >
                    {label}
                  </span>
                </div>
                <p className="mt-1 text-[9.5px] leading-snug text-white/45">{drone.detail}</p>
                {when ? <p className="mt-0.5 font-mono text-[9px] text-white/30">{when}</p> : null}
                {drone.canPush ? (
                  <div className="mt-1.5 flex gap-1.5">
                    <button
                      type="button"
                      disabled={working}
                      onClick={() => push(drone, false)}
                      className="rounded-full border border-white/15 px-2.5 py-[4px] font-mono text-[8.5px] font-bold text-white/70 hover:bg-white/10 disabled:opacity-40"
                    >
                      {working ? 'PUSHING' : 'PUSH ZONES'}
                    </button>
                    {drone.state === 'off' ? (
                      <button
                        type="button"
                        disabled={working}
                        aria-label="SWITCH FENCE ON"
                        title="Sets FENCE_ENABLE to 1 on this aircraft. Its breach action and limits are left as they are."
                        onClick={() => push(drone, true)}
                        className="rounded-full border px-2.5 py-[4px] font-mono text-[8.5px] font-bold disabled:opacity-40"
                        style={{ color: WARN, borderColor: `color-mix(in oklch, ${WARN} 50%, transparent)` }}
                      >
                        SWITCH FENCE ON
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
